import React, { useState, useRef, useEffect } from 'react'
import { Box, Tooltip, IconButton } from '@material-ui/core'
import styled from '@emotion/styled'
import { alpha } from '@material-ui/core/styles'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

const LogoContainer = styled(Box)(({ theme, isDarkMode, isVariantsTablePage, position, isDragging }) => ({
  position: 'fixed',
  left: position ? `${position.x}px` : 'auto',
  top: position ? `${position.y}px` : 'auto',
  right: position ? 'auto' : isVariantsTablePage ? 40 : 25,
  bottom: position ? 'auto' : 40,
  zIndex: 1000,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.48)}`,
  borderRadius: '12px',
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  boxShadow: theme.customShadows.z12,
  width: 56,
  height: 56,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: position
    ? 'none'
    : theme.transitions.create('all', {
        duration: theme.transitions.duration.shorter,
      }),
  '&:hover': {
    transform: position ? 'none' : 'scale(1.1)',
  },
  cursor: isDragging ? 'grabbing' : 'pointer',
  '&:active': {
    cursor: 'grabbing',
  },
  '& *': {
    cursor: 'inherit',
  },
}))

const LogoImage = styled.img({
  width: 38,
  height: 38,
})

export default function ChatbotWidgetButton({ onClick, isVariantsTablePage, isDarkMode, onPositionChange }) {
  const logoSrc = '/static/brand/minimal_logo.png'
  const [position, setPosition] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [mouseStartPos, setMouseStartPos] = useState(null)
  const [hasMoved, setHasMoved] = useState(false)
  const containerRef = useRef(null)
  const NAVBAR_HEIGHT = 64

  const POSITION_KEY = 'chatbotWidgetButtonPosition'

  const EDGE_MARGIN = 20
  const WIDGET_SIZE = 56

  const getViewportBounds = () => {
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    return {
      minX: EDGE_MARGIN,
      maxX: viewportWidth - WIDGET_SIZE - EDGE_MARGIN,
      minY: Math.max(viewportHeight / 2, NAVBAR_HEIGHT + EDGE_MARGIN), // Bottom half only
      maxY: viewportHeight - WIDGET_SIZE - EDGE_MARGIN,
    }
  }

  const validatePosition = (pos) => {
    const bounds = getViewportBounds()
    return {
      x: Math.max(bounds.minX, Math.min(pos.x, bounds.maxX)),
      y: Math.max(bounds.minY, Math.min(pos.y, bounds.maxY)),
    }
  }

  useEffect(() => {
    if (!position) {
      const saved = sessionStorage.getItem(POSITION_KEY)
      if (saved) {
        try {
          const parsed = JSON.parse(saved)
          // Validate saved position against current viewport
          const validatedPosition = validatePosition(parsed)
          setPosition(validatedPosition)
          return
        } catch {}
      }

      const container = containerRef.current
      if (container) {
        const rect = container.getBoundingClientRect()
        const validatedPosition = validatePosition({ x: rect.left, y: rect.top })
        setPosition(validatedPosition)
      }
    }
  }, [position])

  useEffect(() => {
    if (position) {
      sessionStorage.setItem(POSITION_KEY, JSON.stringify(position))
      if (onPositionChange) onPositionChange(position)
    }
  }, [position, onPositionChange])

  useEffect(() => {
    const handleResize = () => {
      if (position) {
        const validatedPosition = validatePosition(position)
        if (validatedPosition.x !== position.x || validatedPosition.y !== position.y) {
          setPosition(validatedPosition)
        }
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [position])

  const handleMouseDown = (e) => {
    setMouseStartPos({ x: e.clientX, y: e.clientY })
    setHasMoved(false)
    setIsDragging(true)

    const rect = e.currentTarget.getBoundingClientRect()
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    })

    e.preventDefault()
    e.stopPropagation()
  }

  const handleMouseMove = (e) => {
    if (!isDragging) return

    // Check if we've moved enough to consider it a drag
    if (mouseStartPos) {
      const distance = Math.sqrt(Math.pow(e.clientX - mouseStartPos.x, 2) + Math.pow(e.clientY - mouseStartPos.y, 2))

      if (distance > 5) {
        setHasMoved(true)
      }
    }

    const newX = e.clientX - dragOffset.x
    const newY = e.clientY - dragOffset.y

    // Apply viewport constraints for bottom half with margins
    const bounds = getViewportBounds()

    const clampedX = Math.max(bounds.minX, Math.min(newX, bounds.maxX))
    const clampedY = Math.max(bounds.minY, Math.min(newY, bounds.maxY))

    setPosition({
      x: clampedX,
      y: clampedY,
    })
  }

  const handleMouseUp = (e) => {
    setIsDragging(false)

    // If we haven't moved significantly, treat it as a click
    if (!hasMoved && mouseStartPos) {
      const distance = Math.sqrt(Math.pow(e.clientX - mouseStartPos.x, 2) + Math.pow(e.clientY - mouseStartPos.y, 2))

      if (distance <= 5) {
        onClick?.(e)
      }
    }

    setMouseStartPos(null)
    setHasMoved(false)
  }

  // Add global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, dragOffset, mouseStartPos, hasMoved])

  return (
    <>
      {!isDragging ? (
        <Tooltip title="Chat AI" placement="left">
          <LogoContainer
            ref={containerRef}
            isDarkMode={isDarkMode}
            isVariantsTablePage={isVariantsTablePage}
            position={position}
            isDragging={isDragging}
            onMouseDown={handleMouseDown}
          >
            <IconButton
              color="inherit"
              sx={{
                p: 0,
                position: 'relative',
                cursor: 'inherit',
                '&:hover': {
                  cursor: 'inherit',
                },
                '&:active': {
                  cursor: 'inherit',
                },
              }}
              disableRipple={isDragging}
            >
              <LogoImage src={logoSrc} alt="logo" />
              <AutoAwesomeIcon
                sx={{
                  position: 'absolute',
                  top: '20%',
                  left: '70%',
                  transform: 'translate(-50%, -50%)',
                  color: 'primary.main',
                  width: 18,
                  height: 18,
                }}
              />
            </IconButton>
          </LogoContainer>
        </Tooltip>
      ) : (
        <LogoContainer
          ref={containerRef}
          isDarkMode={isDarkMode}
          isVariantsTablePage={isVariantsTablePage}
          position={position}
          isDragging={isDragging}
          onMouseDown={handleMouseDown}
        >
          <IconButton
            color="inherit"
            sx={{
              p: 0,
              position: 'relative',
              cursor: 'inherit',
              '&:hover': {
                cursor: 'inherit',
              },
              '&:active': {
                cursor: 'inherit',
              },
            }}
            disableRipple={isDragging}
          >
            <LogoImage src={logoSrc} alt="logo" />
            <AutoAwesomeIcon
              sx={{
                position: 'absolute',
                top: '20%',
                left: '70%',
                transform: 'translate(-50%, -50%)',
                color: 'primary.main',
                width: 18,
                height: 18,
              }}
            />
          </IconButton>
        </LogoContainer>
      )}
    </>
  )
}
